import { DisclosureType } from "@eave/shared/types/api";

export const feesTableSectionLabels = (type: DisclosureType) => {
  if (type === DisclosureType.LoanEstimate) {
    return [
      {
        letter: "A",
        label: "Origination Charges",
      },
      {
        letter: "B",
        label: "Services You Cannot Shop For",
      },
      {
        letter: "C",
        label: "Services You Can Shop For",
      },
      {
        letter: "D",
        label: "Total Loan Costs (A + B + C)",
        isTotalRow: true,
      },
      {
        letter: "E",
        label: "Taxes and Other Government Fees",
      },
      {
        letter: "F",
        label: "Prepaids",
      },
      {
        letter: "G",
        label: "Initial Escrow Payment at Closing",
      },
      {
        letter: "H",
        label: "Other",
      },
      {
        letter: "I",
        label: "Total Other Costs (E + F + G + H)",
        isTotalRow: true,
      },
      {
        letter: "J",
        label: "Total Closing Costs",
        isTotalRow: true,
      },
    ];
  } // Closing Disclosure
  return [
    {
      letter: "A",
      label: "Origination Charges",
    },
    {
      letter: "B",
      label: "Services Borrower Did Not Shop For",
    },
    {
      letter: "C",
      label: "Services Borrower Did Shop For",
    },
    {
      letter: "D",
      label: "Total Loan Costs (A + B + C)",
      isTotalRow: true,
    },
    {
      letter: "E",
      label: "Taxes and Other Government Fees",
    },
    {
      letter: "F",
      label: "Prepaids",
    },
    {
      letter: "G",
      label: "Initial Escrow Payment at Closing",
    },
    {
      letter: "H",
      label: "Other",
    },
    {
      letter: "I",
      label: "Total Other Costs (Borrower-Paid) (E + F + G + H)",
      isTotalRow: true,
    },
    {
      letter: "J",
      label: "Total Closing Costs",
      isTotalRow: true,
    },
  ];
};

// The section labels on the actual disclosure PDFs (same for LE/CD)
export const feesTableHeadings = (type: DisclosureType) => {
  // Util function: given a percentage, returns a style object that sets width
  const columnWidthFromPercentage = num => {
    return {
      width: `${num}%`,
    };
  };

  // Map styles to corresponding percentages (fyi - should not add up to 100%)
  const s = {
    description: columnWidthFromPercentage(17),
    mismo_type: columnWidthFromPercentage(17),
    tolerance: columnWidthFromPercentage(9),
    recipient: columnWidthFromPercentage(9),
    paidToBy: columnWidthFromPercentage(8),
    currencyValue: columnWidthFromPercentage(9),
    apr: columnWidthFromPercentage(6),
    poc: columnWidthFromPercentage(5),
  };

  if (type === DisclosureType.LoanEstimate) {
    return [
      {
        header: "Description",
        key: "description",
        style: s.description,
      },
      {
        header: "Fee Type",
        key: "mismo_type",
        style: s.mismo_type,
      },
      {
        header: "Tolerance",
        key: "tolerance",
        style: s.tolerance,
      },
      {
        header: "In APR?",
        key: "included_in_apr",
        style: s.apr,
      },
      {
        header: "Paid By Type",
        key: "paid_by_type",
        style: s.paidToBy,
      },
      {
        header: "Paid To Type",
        key: "paid_to_type",
        style: s.paidToBy,
      },
      {
        header: "Final LE Amount",
        key: "estimated_amount",
        style: s.currencyValue,
      },
      {
        header: "POC",
        key: "paid_before_closing",
        style: s.poc,
      },
    ];
  }

  if (type === DisclosureType.ClosingDisclosure) {
    return [
      {
        header: "Description",
        key: "description",
        style: s.description,
      },
      {
        header: "Fee Type",
        key: "mismo_type",
        style: s.mismo_type,
      },
      {
        header: "Tolerance",
        key: "tolerance",
        style: s.tolerance,
      },
      {
        header: "In APR?",
        key: "included_in_apr",
        style: s.apr,
      },
      {
        header: "Recipient",
        key: "recipient",
        style: s.recipient,
      },
      {
        header: "Paid By Type",
        key: "paid_by_type",
        style: s.paidToBy,
      },
      {
        header: "Paid To Type",
        key: "paid_to_type",
        style: s.paidToBy,
      },
      {
        header: "Final LE Amount",
        key: "estimated_amount",
        style: s.currencyValue,
      },
      {
        header: "Actual Amount",
        key: "actual_amount",
        style: s.currencyValue,
      },
      {
        header: "POC",
        key: "paid_before_closing",
        style: s.poc,
      },
    ];
  }

  return null;
};

export interface CICShape {
  code: string;
  other_description: string;
  fee_section: string;
}

export const CIC_CODE_OPTIONS = [
  {
    value: "LoanAmountChangedPerBorrowerRequest",
    label: "Loan amount changed per borrower request",
  },
  {
    value: "LoanProductChangedPerBorrowerRequest",
    label: "Loan product changed per borrower request",
  },
  {
    value: "RateLockedAfterFloatPeriod",
    label: "Rate locked after float period",
  },
  {
    value: "RateChangedNotDueToFloatPeriod",
    label: "Rate changed but not due to float period",
  },
  {
    value: "SettlementServiceProviderChangedPerBorrowerRequest",
    label: "Settlement service provider changed per borrower request",
  },
  {
    value: "APRIncreasedByMoreThan0PT125Percent",
    label: "APR increased by more than 0.125%",
  },
  {
    value: "LoanEstimateExpired",
    label: "Previous loan estimate has expired",
  },
  {
    value: "Other",
    label: "Other",
  },
];
